import { Pencil, Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { StatusBadge, Empty } from "@/components/common";
import { fmtDate, fmtEur, BILLINGS, COMMITMENTS, billingPeriodCost, unitBillingPeriodCost } from "@/lib/api";

export default function SubscriptionsTable({ items, pages, onEdit, onDelete }) {
  if (!items.length) return <Empty text="Nessun abbonamento presente." />;
  const pageName = (id) => pages?.find((p) => p.id === id)?.title || "—";
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead>Servizio</TableHead>
            {pages && <TableHead>Pagina</TableHead>}
            <TableHead className="text-right">Licenze</TableHead>
            <TableHead className="text-right">Prezzo unitario</TableHead>
            <TableHead className="text-right">Totale riga</TableHead>
            <TableHead>Obbligo</TableHead>
            <TableHead>Inizio</TableHead>
            <TableHead>Rinnovo Servizio</TableHead>
            <TableHead className="border-l-2 border-slate-600 bg-blue-500/[0.06]" data-testid="billing-group-start">Rinnovo/Fatturazione automatico</TableHead>
            <TableHead className="text-right bg-blue-500/[0.06]">Costo Unitario Periodo di Fatturazione</TableHead>
            <TableHead className="text-right bg-blue-500/[0.06] border-r-2 border-slate-600">Costo Periodo di Fatturazione</TableHead>
            <TableHead>Stato</TableHead>
            {onEdit && <TableHead />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((s) => (
            <TableRow key={s.id} className="border-slate-800 hover:bg-slate-800/30" data-testid={`subscription-row-${s.id}`}>
              <TableCell>
                <div className="font-medium text-slate-100">{s.service_name}</div>
                {s.description && <div className="text-xs text-slate-500 mt-0.5">{s.description}</div>}
              </TableCell>
              {pages && <TableCell className="text-slate-400">{pageName(s.page_id)}</TableCell>}
              <TableCell className="text-right font-mono">{s.quantity}</TableCell>
              <TableCell className="text-right whitespace-nowrap font-mono text-slate-300" data-testid={`unit-price-${s.id}`}>{fmtEur(s.unit_price)}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <div className="font-mono font-medium" data-testid={`row-total-${s.id}`}>{fmtEur(s.price)}</div>
                <div className="text-[11px] text-slate-500">canone {(COMMITMENTS[s.commitment || s.billing] || "").toLowerCase()}</div>
              </TableCell>
              <TableCell className="whitespace-nowrap" data-testid={`commitment-${s.id}`}>
                <span className="inline-flex items-center rounded-sm border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-300" title="Durata minima del contratto: recesso solo alla scadenza naturale">{COMMITMENTS[s.commitment || s.billing] || s.commitment}</span>
              </TableCell>
              <TableCell className="whitespace-nowrap text-slate-400">{fmtDate(s.start_date)}</TableCell>
              <TableCell className="whitespace-nowrap">
                <div>{fmtDate(s.renewal_date)}</div>
                {s.renewals_count > 0 && <div className="text-[11px] text-emerald-500/80" data-testid={`renewed-${s.id}`}>rinnovato ×{s.renewals_count}</div>}
              </TableCell>
              <TableCell className="whitespace-nowrap border-l-2 border-slate-600 bg-blue-500/[0.04]" data-testid={`auto-renew-${s.id}`}>
                {s.auto_renew === false
                  ? <span className="inline-flex items-center gap-1.5 text-slate-500"><span className="h-1.5 w-1.5 rounded-full bg-slate-600" />No</span>
                  : <span className="inline-flex items-center gap-1.5 text-emerald-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />Sì<span className="text-slate-400">· {BILLINGS[s.billing] || s.billing}</span></span>}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap bg-blue-500/[0.04]">
                <div className="font-mono text-slate-300" data-testid={`unit-billing-cost-${s.id}`}>{fmtEur(unitBillingPeriodCost(s))}</div>
                <div className="text-[11px] text-slate-500">a licenza · {(BILLINGS[s.billing] || s.billing || "").toLowerCase()}</div>
              </TableCell>
              <TableCell className="text-right whitespace-nowrap bg-blue-500/[0.04] border-r-2 border-slate-600">
                <div className="font-mono text-slate-100" data-testid={`billing-cost-${s.id}`}>{fmtEur(billingPeriodCost(s))}</div>
                <div className="text-[11px] text-slate-500">{(BILLINGS[s.billing] || s.billing || "").toLowerCase()}</div>
              </TableCell>
              <TableCell><StatusBadge status={s.status} /></TableCell>
              {onEdit && (
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => onEdit(s)} data-testid={`edit-subscription-${s.id}`}><Pencil size={15} /></Button>
                  <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => onDelete(s)} data-testid={`delete-subscription-${s.id}`}><Trash2 size={15} /></Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
